import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  DocumentIcon,
  DocumentChartBarIcon,
  ChartBarSquareIcon,
  UserGroupIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

function niceName(username) {
  if (!username) return "there";
  return username
    .split(".")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : ""))
    .join(" ");
}

const greetingFor = (hour) => {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const TILES = [
  {
    key: "PaymentRequests",
    title: "Payment Requests",
    desc: "Create, review, and manage payment requests",
    icon: BanknotesIcon,
    accent: "from-sky-500 to-indigo-500",
    iconBg: "bg-sky-50 text-sky-600",
  },
  {
    key: "OldPaymentRequests",
    title: "Old Payment Requests",
    desc: "Browse archived payment requests",
    icon: ClipboardDocumentCheckIcon,
    accent: "from-slate-500 to-slate-700",
    iconBg: "bg-slate-50 text-slate-600",
  },
  {
    key: "DocumentSignature",
    title: "Document Signature",
    desc: "Upload documents and collect signatures",
    icon: DocumentIcon,
    accent: "from-emerald-500 to-teal-500",
    iconBg: "bg-emerald-50 text-emerald-600",
  },
  {
    key: "TransactionReports",
    title: "Reports",
    desc: "Transaction, fund, and performance reports",
    icon: DocumentChartBarIcon,
    accent: "from-amber-500 to-orange-500",
    iconBg: "bg-amber-50 text-amber-600",
  },
  {
    key: "SBDriveScoring",
    title: "SB Drive",
    desc: "Driver scoring and program management",
    icon: ChartBarSquareIcon,
    accent: "from-fuchsia-500 to-pink-500",
    iconBg: "bg-fuchsia-50 text-fuchsia-600",
  },
  {
    key: "PaymentRequestsAdmin",
    title: "Admin",
    desc: "Administrative controls and user access",
    icon: UserGroupIcon,
    accent: "from-rose-500 to-red-500",
    iconBg: "bg-rose-50 text-rose-600",
  },
];

export default function HomePage() {
  const navigate = useNavigate();

  const username = sessionStorage.getItem("username") || "";
  const department = sessionStorage.getItem("department") || "";

  const accessList = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("hasAccessTo") || "[]");
    } catch {
      return [];
    }
  }, []);

  const accessSet = useMemo(
    () => new Set(accessList.map((n) => n.replace(/\s/g, ""))),
    [accessList],
  );

  const tiles = TILES.filter((t) => accessSet.has(t.key));
  const greeting = greetingFor(new Date().getHours());
  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-sky-500 p-8 shadow-lg">
        <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-white/10 blur-3xl" />

        <div className="relative">
          <p className="text-sm font-medium text-white/80">{today}</p>
          <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">
            {greeting}, {niceName(username)} 👋
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/85 sm:text-base">
            Welcome to the Solidarity Hub.
            {department && (
              <>
                {" "}You're signed in with the{" "}
                <span className="font-semibold text-white">{department}</span>{" "}
                department.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Quick access */}
      <div className="mt-8">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg font-bold text-slate-800">Quick access</h2>
          <span className="text-xs text-slate-500">
            {tiles.length} {tiles.length === 1 ? "module" : "modules"} available
          </span>
        </div>

        {tiles.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
            You don't have access to any modules yet. Please contact an
            administrator.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {tiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.key}
                  onClick={() => navigate(`/hub?page=${tile.key}`)}
                  className="group relative flex flex-col items-start overflow-hidden rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  <div
                    className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${tile.accent}`}
                  />
                  <div
                    className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg ${tile.iconBg}`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-base font-bold text-slate-800">
                    {tile.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">{tile.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 opacity-0 transition group-hover:opacity-100">
                    Open
                    <ArrowRightIcon className="h-4 w-4" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
